import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Crown, TrendingUp, Newspaper, Plus, Trash2, ExternalLink, Loader2, Award } from "lucide-react";
import { toast } from "sonner";

const SENTIMENT = {
  positive: { color: "#22C55E", label: "POSITIVE" },
  neutral: { color: "#94A3B8", label: "NEUTRAL" },
  negative: { color: "#EF4444", label: "NEGATIVE" },
};

const CAT_COLOR = {
  government: "#3B82F6",
  investor: "#F59E0B",
  client: "#22C55E",
  partner: "#A855F7",
  other: "#94A3B8",
};

const fmtGhs = (n) => (n == null ? "—" : n >= 1_000_000 ? `GH₵ ${(n / 1_000_000).toFixed(1)}M` : `GH₵ ${n.toLocaleString()}`);

export default function Reputation() {
  const [rankings, setRankings] = useState([]);
  const [byCat, setByCat] = useState({});
  const [visibility, setVisibility] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [newMention, setNewMention] = useState({ headline: "", source_name: "", source_url: "", sentiment: "neutral", subject: "organization" });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/reputation/rankings${filter ? `?category=${filter}` : ""}`),
      api.get("/reputation/visibility"),
      api.get("/reputation/mentions"),
    ]).then(([r, v, m]) => {
      setRankings(r.data.rankings);
      setByCat(r.data.by_category);
      setVisibility(v.data);
      setMentions(m.data);
    }).finally(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const addMention = async () => {
    if (!newMention.headline.trim()) return;
    try {
      await api.post("/reputation/mentions", newMention);
      setNewMention({ headline: "", source_name: "", source_url: "", sentiment: "neutral", subject: "organization" });
      setAdding(false);
      load();
      toast.success("Media mention logged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteMention = async (id) => {
    try {
      await api.delete(`/reputation/mentions/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reputation-page">
      {/* Header */}
      <div>
        <div className="label-eyebrow mb-1">// Institutional Reputation & Influence</div>
        <h1 className="font-display text-3xl text-white tracking-tight">Reputation Engine</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Stakeholder influence rankings, executive visibility metrics, and the institutional media trail — synthesized
          from your live engagement data.
        </p>
      </div>

      {/* Visibility row */}
      {visibility && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="visibility-metrics">
          <Stat label="Mentions 30d" value={visibility.mentions_30d} eyebrow="01 // VISIBILITY" />
          <Stat label="Mentions 90d" value={visibility.mentions_90d} eyebrow="02 // REACH" />
          <Stat label="Positive 30d" value={visibility.sentiment_30d.positive} eyebrow="03 // SENTIMENT" valueColor="#22C55E" />
          <Stat label="Top Sources" value={visibility.top_sources_90d.length} eyebrow="04 // NETWORK" />
        </div>
      )}

      {/* Rankings + Mentions row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Influence Leaderboard */}
        <div className="lg:col-span-2 nexus-glass-panel p-5" data-testid="influence-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Crown className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
              <span className="label-eyebrow">// Influence Leaderboard</span>
            </div>
            <div className="flex flex-wrap gap-1">
              <FilterChip label="All" active={!filter} onClick={() => setFilter("")} count={rankings.length} />
              {Object.entries(byCat).map(([c, n]) => (
                <FilterChip key={c} label={c} active={filter === c} onClick={() => setFilter(c)} count={n} />
              ))}
            </div>
          </div>
          {loading ? (
            <div className="py-10 text-slate-400 font-mono text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> CALCULATING…</div>
          ) : rankings.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No stakeholders to rank yet.</div>
          ) : (
            <div className="space-y-2">
              {rankings.slice(0, 12).map((r, i) => (
                <motion.div
                  key={r.stakeholder_id}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/15 hover:bg-white/[0.02] transition-all"
                  data-testid={`rank-${r.stakeholder_id}`}
                >
                  <div className="font-mono text-xs text-[#D4AF37] w-6 text-right">{String(i + 1).padStart(2, "0")}</div>
                  <div className="w-1 h-10 shrink-0" style={{ background: CAT_COLOR[r.category] || "#94A3B8" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.organization} · {r.category}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-lg" style={{ color: "var(--gold-400)" }}>
                      {r.influence}
                    </div>
                    <div className="font-mono text-[9px] text-slate-500 tracking-widest">
                      R{r.relationship_score} · C{r.comm_count} · M{r.meeting_count}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Media Mentions */}
        <div className="nexus-glass-panel p-5" data-testid="media-mentions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Newspaper className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
              <span className="label-eyebrow">// Media Mentions</span>
            </div>
            <button
              onClick={() => setAdding(!adding)}
              data-testid="add-mention-toggle"
              className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 hover:border-white/30 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> ADD
            </button>
          </div>

          {adding && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-2 mb-3" data-testid="mention-form">
              <input value={newMention.headline} onChange={(e) => setNewMention({ ...newMention, headline: e.target.value })} placeholder="Headline" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-2.5 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none" data-testid="mention-headline" />
              <input value={newMention.source_name} onChange={(e) => setNewMention({ ...newMention, source_name: e.target.value })} placeholder="Source (e.g., GraphicOnline)" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-2.5 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none" />
              <input value={newMention.source_url} onChange={(e) => setNewMention({ ...newMention, source_url: e.target.value })} placeholder="URL" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-2.5 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none" />
              <select value={newMention.sentiment} onChange={(e) => setNewMention({ ...newMention, sentiment: e.target.value })} className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-2.5 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none">
                <option value="positive">Positive</option><option value="neutral">Neutral</option><option value="negative">Negative</option>
              </select>
              <button onClick={addMention} data-testid="save-mention" className="w-full text-[11px] font-mono tracking-widest py-1.5" style={{ background: "var(--gold-500)", color: "#050B14" }}>SAVE</button>
            </motion.div>
          )}

          {mentions.length === 0 ? (
            <div className="text-[12px] text-slate-500 py-6 text-center">No mentions logged.</div>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {mentions.map((m) => {
                const sent = SENTIMENT[m.sentiment] || SENTIMENT.neutral;
                return (
                  <div key={m.id} className="p-2.5 border border-white/5 hover:border-white/15 transition-all group" data-testid={`mention-${m.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white leading-snug">{m.headline}</div>
                        <div className="flex items-center gap-2 mt-1 text-[10px]">
                          <span className="font-mono tracking-widest" style={{ color: sent.color }}>{sent.label}</span>
                          {m.source_name && <span className="text-slate-500 truncate">· {m.source_name}</span>}
                          {m.source_url && <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline"><ExternalLink className="w-3 h-3 inline" /></a>}
                        </div>
                      </div>
                      <button onClick={() => deleteMention(m.id)} className="text-slate-600 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, eyebrow, valueColor }) => (
  <div className="nexus-card p-4">
    <div className="label-eyebrow">{eyebrow}</div>
    <div className="font-display text-2xl mt-1" style={{ color: valueColor || "#F8FAFC" }}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

const FilterChip = ({ label, active, onClick, count }) => (
  <button onClick={onClick} className={`px-2 py-1 border font-mono text-[10px] tracking-widest transition-all ${active ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white" : "border-white/10 text-slate-400 hover:border-white/25"}`}>
    {label.toUpperCase()} <span className="text-slate-500">{count}</span>
  </button>
);
